/**
 * AppV2 - Telegram tab-based application shell
 * Telegram auto-registration, deep link handling, tab navigation
 */

import { useEffect, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';
import { useTabBackButton } from '@shared/hooks/useTabBackButton';
import { useLoadPreferences } from '@shared/hooks/useLoadPreferences';
import { useSubscriptionInfo } from '@shared/hooks/useSubscriptionInfo';
import { useAuthStore } from '@shared/store/authStore';
import { useTabNavigationStore } from '@shared/store/tabNavigationStore';
import { useThemeStore } from '@shared/store/themeStore';
import { usersApi } from '@shared/api/users';
import { isMaxMiniApp } from '@shared/api/miniAppAuth';

// Layout
import { AppShell } from '@shared/components/ui/AppShell';
import { LiveRedesign, detectPlatform, isRedesignEnabled, useLegacyLaunchReport } from '@shared/redesign';

// Tab renderers
import {
  renderHomeStack,
  renderAthletesStack,
  renderGroupsStack,
  renderProfileStack,
} from './navigation/tabRenderers';

// Bottom sheet content
import { AIAssistantSheet } from '@shared/screens/Hub/AIAssistantSheet';
import { OrgSwitcherSheet } from '@shared/screens/Sheets/OrgSwitcherSheet';
import { DirectionSwitcherSheet } from '@shared/screens/Sheets/DirectionSwitcherSheet';

// QueryClient singleton
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const isNetworkError =
          error && typeof error === 'object' && 'error' in error &&
          (error as { error?: string }).error === 'Network error';
        if (isNetworkError) return failureCount < 3;
        return failureCount < 1;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      refetchOnReconnect: true,
    },
  },
});

function AppV2Content() {
  const { user: telegramUser } = useTelegramWebApp();
  const { user, setUser } = useAuthStore();
  const { activeTab, getCurrentScreen, switchTab, pushScreen } = useTabNavigationStore();
  const { isDark, setTheme } = useThemeStore();

  // Telegram BackButton integration
  useTabBackButton();

  // Load preferences from server on startup
  const { isLoading: isLoadingPreferences } = useLoadPreferences();

  // Load subscription info (limits, usage, grace status) into authStore
  useSubscriptionInfo();

  // Initialize theme on mount
  useEffect(() => {
    setTheme(isDark);
  }, []);

  // Auto-registration via Telegram
  useEffect(() => {
    const isValidUser = user && user.id;

    // MAX: пользователя по подписанной init data находит (или регистрирует,
    // как бот MAX) сервер, тело запроса ему не нужно
    if (!telegramUser && !isValidUser && isMaxMiniApp()) {
      usersApi
        .getMe()
        .then(setUser)
        .catch((error) => {
          console.error('Failed to load MAX user:', error);
        });
      return;
    }

    if (telegramUser && !isValidUser) {
      usersApi
        .register({
          telegram_user_id: telegramUser.id,
          telegram_username: telegramUser.username,
          name:
            telegramUser.first_name +
            (telegramUser.last_name ? ` ${telegramUser.last_name}` : ''),
        })
        .then((registeredUser) => {
          setUser(registeredUser);
        })
        .catch((error) => {
          console.error('Failed to register user:', error);
        });
    }
  }, [telegramUser, user, setUser]);

  // Deep link handling (org_invite_)
  useEffect(() => {
    if (!user) return;
    if (isLoadingPreferences) return;

    const hash = window.location.hash.slice(1);

    if (hash && hash.startsWith('org_invite_')) {
      const fullCode = hash.replace('org_invite_', '');
      const inviteCode = fullCode.split('?')[0];

      window.location.hash = '';

      switchTab('profile');
      pushScreen('join-organization', { inviteCode });
      return;
    }
  }, [user, switchTab, pushScreen, isLoadingPreferences]);

  const redesign = useMemo(() => isRedesignEnabled(), []);
  const redesignPlatform = useMemo(() => detectPlatform(), []);
  // Счётчик открытий прежнего интерфейса (редизайн, этап 5): одно событие на запуск
  useLegacyLaunchReport(!redesign && !!user?.id, redesignPlatform.kind);

  // Render active tab content
  const currentScreen = getCurrentScreen();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'home':
        return renderHomeStack(currentScreen);
      case 'athletes':
        return renderAthletesStack(currentScreen);
      case 'groups':
        return renderGroupsStack(currentScreen);
      case 'profile':
        return renderProfileStack(currentScreen);
      default:
        return renderHomeStack(currentScreen);
    }
  };

  // Show loading until user is validated
  const isValidUser = user && user.id;

  if (!isValidUser) {
    return (
      <div
        className="flex items-center justify-center h-screen"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        <p style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
      </div>
    );
  }

  // Новый дизайн включается вручную на устройстве (см. redesign/flag.ts).
  if (redesign) {
    return <LiveRedesign platform={redesignPlatform} router="memory" preferencesLoading={isLoadingPreferences} />;
  }

  return (
    <AppShell
      hubContent={<AIAssistantSheet />}
      orgSwitcherContent={<OrgSwitcherSheet />}
      directionSwitcherContent={<DirectionSwitcherSheet />}
    >
      {renderTabContent()}
    </AppShell>
  );
}

function AppV2() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppV2Content />
    </QueryClientProvider>
  );
}

export default AppV2;
